import {PropsWithChildren, forwardRef, useImperativeHandle, useCallback, SetStateAction} from "react";
import MaybeNotSetupUserContext, {IMaybeNotSetupUserContext} from "./maybeNotSetupUserContext";
import {IUser} from "api/types";
import useAuthContext from "contexts/Auth/useAuthContext";
import api from "api";
import FullLoader from "components/loaders/FullLoader";
import {useQuery, useQueryClient} from "@tanstack/react-query";

const MaybeNotSetupUserProvider = forwardRef<IMaybeNotSetupUserContext, PropsWithChildren>(({children}, ref) => {
  const auth = useAuthContext();
  const queryClient = useQueryClient();

  const {data, isLoading} = useQuery({
    queryKey: ["profile", auth?.uid],
    queryFn: () => api.getProfile(),
    enabled: !!auth,
    staleTime: Infinity,
    retry: false,
  });

  const user: IUser | null | undefined = !auth ? null : isLoading ? undefined : (data ?? null);

  const setUser: IMaybeNotSetupUserContext["setUser"] = useCallback(
    (valueOrUpdater: SetStateAction<IUser | null | undefined>) => {
      queryClient.setQueryData<IUser | null>(["profile", auth?.uid], valueOrUpdater);
    },
    [auth?.uid, queryClient],
  );

  useImperativeHandle(ref, () => ({user, setUser}), [user, setUser]);

  return (
    <MaybeNotSetupUserContext.Provider value={{user, setUser}}>
      {user === undefined ? <FullLoader /> : children}
    </MaybeNotSetupUserContext.Provider>
  );
});

export default MaybeNotSetupUserProvider;
